import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useState } from 'react';

import { uploadPhoto } from '../BackendRoute';
import FloatingUploadButton from '../components/FloatingUploadButton';
import PhotoLightbox from '../components/PhotoLightbox';
import PhotoSyncBar from '../components/PhotoSyncBar';
import PhotoThumbnail from '../components/PhotoThumbnail';
import { usePhotoFeed } from '../hooks/usePhotoFeed';

export default function PhotoBoardPage({ eventType }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Custom hook abstracting photo feed data, pagination, and sync via after_uploaded_at
  const {
    photos,
    hasNext,
    loadingPhotos,
    isRefreshing,
    lastRefreshedAt,
    loadMore,
    syncPhotos,
    addPhoto,
  } = usePhotoFeed({
    eventType,
    isPaused: uploading,
    onNewPhotos: (count) => {
      // Shift active lightbox index so the viewed photo doesn't jump when new items arrive
      setSelectedIndex((prev) => (prev !== null ? prev + count : null));
    },
  });

  const selectedPhoto = selectedIndex !== null ? photos[selectedIndex] : null;
  const hasPrev = selectedIndex !== null && selectedIndex > 0;
  const hasNextPhoto =
    selectedIndex !== null && selectedIndex < photos.length - 1;

  const handlePrev = () => {
    if (hasPrev) setSelectedIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (hasNextPhoto) setSelectedIndex((prev) => prev + 1);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadError(null);

    const MAX_SIZE_MB = 50;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    const oversizedFiles = files.filter((file) => file.size > MAX_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
      const names = oversizedFiles.map((f) => `"${f.name}"`).join(', ');
      setUploadError(
        `File ${names} ${oversizedFiles.length > 1 ? 'exceed' : 'exceeds'} the ${MAX_SIZE_MB}MB size limit.`,
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Upload sequentially so each one shows individual progress
    for (let i = 0; i < files.length; i++) {
      try {
        const newPhoto = await uploadPhoto(files[i], eventType, null, (pct) =>
          setUploadProgress(Math.round(((i + pct / 100) / files.length) * 100)),
        );
        addPhoto(newPhoto);
        setSelectedIndex((prev) => (prev !== null ? prev + 1 : null));
      } catch (err) {
        const backendMsg = err.response?.data?.error || err.message;
        setUploadError(
          backendMsg
            ? `Failed to upload "${files[i].name}": ${backendMsg}`
            : `Failed to upload "${files[i].name}".`,
        );
      }
    }

    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="@container w-full min-h-full p-6 text-center bg-wedding-blush flex flex-col items-center">
      <p className="text-xl text-content-c2 mb-3">
        Share your moments with us! 📸
      </p>

      {/* Auto-sync status indicator and manual refresh button */}
      <PhotoSyncBar
        isRefreshing={isRefreshing}
        lastRefreshedAt={lastRefreshedAt}
        disabled={loadingPhotos || uploading}
        onRefresh={syncPhotos}
      />

      {/* Upload progress bar */}
      {uploading && (
        <div className="w-full max-w-sm h-2 bg-gray-200 rounded-full mb-4 overflow-hidden">
          <motion.div
            className="h-full bg-rose-400 rounded-full"
            animate={{ width: `${uploadProgress}%` }}
            transition={{ ease: 'linear' }}
          />
        </div>
      )}

      {uploadError && (
        <p className="text-red-500 text-sm mb-4">{uploadError}</p>
      )}

      {/* Photo grid */}
      <div className="grid grid-cols-2 @[500px]:grid-cols-3 gap-3 w-full max-w-4xl">
        <AnimatePresence>
          {photos.map((photo, idx) => (
            <motion.button
              key={photo.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              className="aspect-square rounded-xl overflow-hidden shadow-md focus:outline-none focus:ring-2 focus:ring-rose-300"
              onClick={() => setSelectedIndex(idx)}
              aria-label="View image"
            >
              <PhotoThumbnail
                src={photo.thumbnail_url}
                alt="Guest upload thumbnail"
              />
            </motion.button>
          ))}
        </AnimatePresence>

        {!loadingPhotos && photos.length === 0 && (
          <div className="col-span-full text-content-c2 italic py-12">
            No photos yet. Be the first to upload!
          </div>
        )}
      </div>

      {/* Load more */}
      {hasNext && (
        <button
          onClick={loadMore}
          disabled={loadingPhotos || isRefreshing}
          className="btn-wedding-primary mt-8"
        >
          {loadingPhotos ? 'Loading…' : 'Load more'}
        </button>
      )}

      {!hasNext && photos.length > 0 && !loadingPhotos && (
        <p className="text-content-c2 italic mt-8 text-sm">
          No more images available
        </p>
      )}

      {loadingPhotos && (
        <p className="text-content-c2 mt-8 animate-pulse">Loading photos…</p>
      )}

      {/* Floating Upload Button */}
      <FloatingUploadButton
        fileInputRef={fileInputRef}
        onFileChange={handleFileChange}
        uploading={uploading}
        uploadProgress={uploadProgress}
      />

      {/* Lightbox */}
      <AnimatePresence>
        {selectedPhoto && (
          <PhotoLightbox
            photo={selectedPhoto}
            hasPrev={hasPrev}
            hasNext={hasNextPhoto}
            onPrev={handlePrev}
            onNext={handleNext}
            onClose={() => setSelectedIndex(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
